import { createServerFn } from '@tanstack/react-start';
import { Resource } from 'sst';
import { putDraftNgos, createDraft, createPresignedUploadUrl } from '@worldcoo/monolith/apis/backoffice/server';
import { captcha } from '@worldcoo/monolith/captcha';
import * as z from 'zod';
import { parseIp } from './server-functions-shared.server';

export type OnboardingDraftErrorCode = 'NETWORK_ERROR' | 'SAVE_FAILED';

const onboardingFileTypes = {
	onboardingLogo: { prefix: (id: string) => `ngos/${id}/logo` },
	onboardingCertificate: { prefix: (id: string) => `ngos/${id}/certificate` },
} as const;

type OnboardingFileType = keyof typeof onboardingFileTypes;

/** Presigned URL endpoint for onboarding (unauthenticated, captcha-protected). */
export const getOnboardingPresignedUrlFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			ngoId: z.string(),
			contentType: z.string(),
			fileType: z.enum(['onboardingLogo', 'onboardingCertificate'] as [OnboardingFileType, ...OnboardingFileType[]]),
			captchaToken: z.string(),
		}),
	)
	.handler(async ({ data: { ngoId, contentType, fileType, captchaToken } }) => {
		const requestIp = parseIp();
		if (!requestIp) return { error: 'CAPTCHA_FAILED' as const };
		const captchaResult = await captcha.validate(captchaToken, requestIp);
		if (!captchaResult.captchaPassed) return { error: 'CAPTCHA_FAILED' as const };

		const config = onboardingFileTypes[fileType];
		const prefix = config.prefix(ngoId);
		const result = await createPresignedUploadUrl(prefix, contentType);
		if (!result) return { error: 'INVALID_CONTENT_TYPE' as const };
		return result;
	});

export const createOnboardingDraftFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			data: z.record(z.string(), z.unknown()),
			captchaToken: z.string(),
		}),
	)
	.handler(async ({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) return { error: 'NETWORK_ERROR' as const };

		if (Resource.App.stage === 'dev') {
			// In dev, bypass captcha/validation (validator CJS doesn't work in Vite SSR)
			// Logo and bankAccountCertificate are S3 keys (already uploaded via presigned URL)
			try {
				// TODO: remove cast when onboarding schema can be imported without i18n dependency
				await putDraftNgos(data.data as Parameters<typeof putDraftNgos>[0]);
			} catch (err) {
				console.error('Draft save failed:', err instanceof Error ? err.message : String(err));
				return { error: 'SAVE_FAILED' as const };
			}
			return { captchaPassed: true, success: true };
		}

		// TODO: remove cast when onboarding schema can be imported without i18n dependency
		return await createDraft(data as Parameters<typeof createDraft>[0], requestIp);
	});
